/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { VisibilityState } from 'src/store/visibilityevents/visibilitystate';
import { Locale, LocaleKeys } from '../../../core/locale';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../core/navigation';
import { TScreen } from '../../../core/navigation/types';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import { setListEntryFocus } from '../../../store/list/list.actions';
import { setSettingsOptionsList } from '../../../store/settings/mainsettings/mainsettings.reducers';
import { List, Mousetrap, ScreenPreview } from '../../common';
import { useScreenController } from '../../hooks';
import { SettingsTemplate } from '../SettingsTemplate';

import '../SettingsCommon.scss';

const MainSettings: FunctionComponent = (): JSX.Element => {
    const TAG = 'MainSettings';

    const dispatch = useTivoDispatch();
    const { visibilityState } = useTivoSelector((state: RootState) => state.visibilityEvents);

    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.MAIN_SETTINGS_SCREEN,
            screenController: ScreenControllerTypes.MAIN_SETTINGS_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS,
        },
    });

    const [isCursorActive, setisCursorActive] = useState(false);
    const { optionsList, selectedOption } = useTivoSelector(
        (state: RootState) => state.mainSettings,
    );
    const selectedOptionIndex = useMemo(() => {
        return optionsList.reduce(
            (acc, option, index) => (option.title === selectedOption?.title ? index : acc),
            0,
        );
    }, [optionsList, selectedOption]);

    const onCursorStateChangeCallback = (state) => {
        setisCursorActive(state);
        if (!isCursorActive) dispatch(setListEntryFocus(ListTypes.MAIN_SETTINGS, true));
    };

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_RESUME) {
            // When the screen is resumed,the cursor state is updated
            onCursorStateChangeCallback(isCursorActive);
        }
    }, [visibilityState]);

    useEffect(() => {
        dispatch(setSettingsOptionsList());
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_DEFAULT)}>
            <div className="settings-common-content">
                <List
                    type={ListTypes.MAIN_SETTINGS}
                    itemList={optionsList}
                    initialFocusedIndex={selectedOptionIndex}
                    initialSelectedIndex={selectedOptionIndex}
                    generateItemView={(item: any, itemClass, index) => (
                        <Mousetrap
                            key={index}
                            value={`${index}_${ListTypes.MAIN_SETTINGS}`}
                            className={`item ${itemClass}`}
                        >
                            <span>{item.title}</span>
                        </Mousetrap>
                    )}
                />
            </div>
            <ScreenPreview>
                <div>{selectedOption?.preview}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default MainSettings;
